import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  MapPin,
  Clock,
  Briefcase,
  CheckCircle,
} from "lucide-react"
import { Link } from "react-router-dom"

const positions = [
  {
    id: "cto-executive",
    title: "Chief Technology Officer (CTO)",
    department: "Executive Leadership",
    location: "Dallas-Fort Worth, USA",
    type: "Full-time",
    experience: "10+ Years",
    description:
      "Define and execute the company's technology strategy while actively supporting and enabling the sales function through technical leadership, solution design, and customer-facing engagement. This role bridges engineering, product, and sales, ensuring that technology directly contributes to revenue growth and customer success.",
    requirements: [
      "Master's degree in Computer Science, Engineering, or related field; MBA is a plus",
      "Proven experience in technical leadership (VP of Engineering, prior CTO roles)",
      "Experience supporting sales-facing technical roles (Solution Architect, Presales Engineer, Field CTO)",
      "Strong communication skills to explain complex technology to executives and non-technical stakeholders",
      "Familiarity with sales cycles, CRM systems, and revenue metrics",
      "Experience defining technology vision, roadmap, infrastructure, and architecture",
      "Track record of building and mentoring high-performing engineering teams",
      "Background in security, compliance, and disaster-recovery planning",
    ],
  },
  {
    id: "marketing-intern-le",
    title: "Marketing And Branding Intern",
    department: "Marketing",
    location: "LittleElm, TX",
    type: "Internship",
    experience: "Entry Level",
    description:
      "Join our marketing team to help shape the RSA Tech Group brand. You will assist in content creation, social media strategy, and market research to amplify our presence in the enterprise technology space. Note: This position is fully onsite in LittleElm and does not offer visa sponsorship.",
    requirements: [
      "Required Bachelors in Marketing or Relevant Field Study",
      "Strong written and verbal communication skills",
      "Familiarity with social media platforms (LinkedIn, Twitter/X)",
      "Basic understanding of B2B marketing concepts",
      "Creative mindset and eagerness to learn",
      "Must be located in LittleElm, TX area (Onsite required)",
    ],
  },
  {
    id: "backend-python-hyd",
    title: "Backend Developer",
    department: "Engineering",
    location: "Hyderabad, India",
    type: "Full-time",
    experience: "5+ Years",
    description:
      "We are seeking a highly skilled Backend Developer with deep expertise in Python ecosystems to build scalable, high-performance infrastructure for our AI platforms.",
    requirements: [
      "Extensive experience with Python and related tech stack (Django/FastAPI/Flask)",
      "Minimum 5+ years of professional experience",
      "Proven track record in high-growth startup environments (Preferred)",
      "Deep understanding of distributed systems and database architecture",
      "Experience with cloud-native technologies (AWS/Azure/GCP)",
    ],
  },
  {
    id: "fullstack-python-ui-hyd",
    title: "Full Stack Developer",
    department: "Engineering",
    location: "Hyderabad, India",
    type: "Full-time",
    experience: "3+ Years",
    description:
      "We are seeking a versatile Full Stack Developer who bridges the gap between sophisticated Python backends and modern, responsive UIs. You will own features from database schema design to pixel-perfect frontend implementation.",
    requirements: [
      "Strong proficiency in Frontend technologies (React, TypeScript, Tailwind CSS)",
      "Solid backend experience with Python ecosystems (Django/FastAPI)",
      "Minimum 3+ years of full-stack development experience",
      "Experience interacting with RESTful APIs and managing application state",
      "Ability to translate design mockups into functional, high-quality code",
    ],
  },
  {
    id: "ai-ml-engineer-hyd",
    title: "AI/ML Engineer",
    department: "Engineering",
    location: "Hyderabad, India",
    type: "Full-time",
    experience: "3+ Years",
    description:
      "We are looking for an innovative AI/ML Engineer to design and deploy machine learning models that power our next-generation intelligent applications. You will work with large datasets, build training pipelines, and optimize models for production inference.",
    requirements: [
      "Strong proficiency in Python and ML frameworks (PyTorch, TensorFlow, Scikit-learn)",
      "Experience with NLP, Computer Vision, or Generative AI (LLMs)",
      "Minimum 3+ years of experience in Applied Machine Learning",
      "Familiarity with MLOps pipelines and model deployment (Kubeflow, MLflow)",
      "Strong background in mathematics, statistics, and algorithm design",
    ],
  },
  {
    id: "intern-dfw",
    title: "Software Engineering Intern",
    department: "Engineering",
    location: "Dallas-Fort Worth, USA",
    type: "Internship",
    experience: "Entry Level",
    description:
      "Launch your career with RSA Tech Group. We provide comprehensive 1-month training followed by immediate deployment into ongoing product development cycles. Note: This position is fully onsite in DFW and does not offer visa sponsorship.",
    requirements: [
      "Strong coding skills in Python",
      "Foundational knowledge in AI/Machine Learning concepts",
      "Minimum Bachelor's Degree in Computer Science (or related) from a US university",
      "Must be located in Dallas-Fort Worth area (Onsite required)",
      "No Sponsorship Available at this time",
    ],
  },
]

export const OpenPositions = () => {
  return (
    <section id="open-positions" className="relative py-24 bg-[#0f1629] overflow-hidden">
      {/* Background overlays */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(28,43,110,0.4),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(28,43,110,0.25),transparent_70%)]" />

      {/* Glow orbs */}
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px]" />

      <div className="section-container relative z-10">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-[#FDA400]" />
            <span className="text-[#FDA400] text-xs font-semibold uppercase tracking-[0.2em]">
              Join Our Team
            </span>
            <div className="h-px w-10 bg-[#FDA400]" />
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
            Open <span className="text-[#FDA400]">Positions</span>
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed font-light">
            Join a team of visionaries and builders. We are looking for
            exceptional talent to help us define the future of enterprise AI.
          </p>
        </div>

        <div className="space-y-6">
          {positions.map((position) => (
            <div
              key={position.id}
              className="group bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] hover:border-white/[0.15] rounded-2xl p-8 transition-all duration-300 hover:shadow-[0_25px_80px_rgba(0,0,0,0.3)] backdrop-blur-sm"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div className="space-y-4 flex-1">
                  <div>
                    <h3 className="text-2xl font-bold text-white group-hover:text-[#FDA400] transition-colors mb-2">
                      {position.title}
                    </h3>
                    <div className="flex flex-wrap gap-3 text-sm text-slate-400 font-medium">
                      <span className="flex items-center gap-1.5 bg-white/[0.06] px-3 py-1 rounded-full border border-white/[0.08]">
                        <Briefcase className="w-4 h-4" /> {position.department}
                      </span>
                      <span className="flex items-center gap-1.5 bg-white/[0.06] px-3 py-1 rounded-full border border-white/[0.08]">
                        <MapPin className="w-4 h-4" /> {position.location}
                      </span>
                      <span className="flex items-center gap-1.5 bg-white/[0.06] px-3 py-1 rounded-full border border-white/[0.08]">
                        <Clock className="w-4 h-4" /> {position.type}
                      </span>
                      <span className="flex items-center gap-1.5 bg-white/[0.06] px-3 py-1 rounded-full border border-white/[0.08]">
                        <CheckCircle className="w-4 h-4" />{" "}
                        {position.experience}
                      </span>
                    </div>
                  </div>

                  <p className="text-slate-400 leading-relaxed max-w-3xl">
                    {position.description}
                  </p>

                  <ul className="grid md:grid-cols-2 gap-2">
                    {position.requirements.map((req, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-slate-400"
                      >
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FDA400] flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex-shrink-0 pt-2">
                  <Link to="/careers/apply">
                    <Button
                      size="lg"
                      className="w-full md:w-auto bg-[#FDA400] hover:bg-[#FDA400]/90 text-[#1C2B6E] font-bold transition-colors shadow-lg shadow-[#FDA400]/20"
                    >
                      Apply Now <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
